import { ROSTER_CATEGORY_OPTIONS, rosterCategoryOf } from '@/components/RosterFilters';
import type { FeeAccountListParams, FeeHead } from '@/types/fees';
import type { InstituteClass } from '@/types/instituteClass';
import { FEE_HEAD_LABEL } from './constants';

/**
 * What the summary cards are counted over: the register's own filters, less
 * the fee status and the sort.
 *
 * The cards follow the filters above the table, so narrowing the register to
 * Class 5 narrows the totals with it. The fee status is left out on purpose:
 * "collected, among those who paid nothing" is always zero, and the "Not paid"
 * card sets that status itself — it must not empty the four beside it.
 */
export type FeeSummaryScope = Pick<
  FeeAccountListParams,
  'search' | 'classUuid' | 'sectionUuid' | 'gender' | 'newThisYear' | 'head'
>;

export function feeSummaryScope(filters: FeeAccountListParams, head: FeeHead): FeeSummaryScope {
  return {
    search: filters.search ?? '',
    classUuid: filters.classUuid ?? '',
    sectionUuid: filters.sectionUuid ?? '',
    gender: filters.gender ?? '',
    newThisYear: filters.newThisYear ?? '',
    head,
  };
}

export function isFeeSummaryNarrowed(scope: FeeSummaryScope): boolean {
  return Object.values(scope).some(Boolean);
}

/** `Class 5 - A · Girls · Tuition fee` — what the cards' hints say they are counting. */
export function describeFeeSummary(scope: FeeSummaryScope, classes: InstituteClass[]): string | null {
  if (!isFeeSummaryNarrowed(scope)) return null;

  const instituteClass = classes.find((one) => one.uuid === scope.classUuid);
  const section = instituteClass?.sections.find((one) => one.uuid === scope.sectionUuid);
  const category = ROSTER_CATEGORY_OPTIONS.find((option) => option.value === rosterCategoryOf(scope));

  return [
    instituteClass && (section ? `${instituteClass.name} - ${section.name}` : instituteClass.name),
    category?.value ? category.label : null,
    scope.head ? FEE_HEAD_LABEL[scope.head] : null,
    scope.search ? `“${scope.search}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
